import { useState, useEffect } from 'react';
import Input from '@components/ui/Input';
import Button from '@components/ui/Button';
import MultiSelectAutocomplete from '@components/ui/MultiSelectAutocomplete';
import { listProfiles, listSecretarias } from '@services/userService';
import { FormControlLabel, Checkbox } from '@mui/material';

export default function UserForm({ user, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    profile_id: '',
    is_active: true,
    password: '',
    secretaria_ids: [],
    allow_all_secretarias: true, // Marcado por padrão
  });
  const [profiles, setProfiles] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profilesData, secretariasData] = await Promise.all([
          listProfiles(),
          listSecretarias()
        ]);
        setProfiles(profilesData || []);
        setSecretarias(secretariasData || []);

        // Não pré-seleciona perfil, deixa o usuário escolher
        // if (!user && profilesData && profilesData.length > 0) {
        //   setFormData((prev) => ({ ...prev, profile_id: profilesData[0].id }))
        // }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        alert('Erro ao carregar perfis e secretarias. Por favor, recarregue a página.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        profile_id: user.profile?.id || '',
        is_active: user.is_active,
        password: '',
        secretaria_ids: user.secretarias?.map(s => s.id) || [],
        allow_all_secretarias: user.allow_all_secretarias || false,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSecretariasChange = (selectedSecretarias) => {
    const secretariaIds = selectedSecretarias.map(name => {
      const sec = secretarias.find(s => s.secretaria === name);
      return sec?.id;
    }).filter(id => id !== null && id !== undefined);
    setFormData(prev => ({ ...prev, secretaria_ids: secretariaIds }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validação adicional do perfil
    if (!formData.profile_id) {
      alert('Por favor, selecione um perfil para o usuário.');
      return;
    }

    // Validação de secretarias
    if (!formData.allow_all_secretarias && (!formData.secretaria_ids || formData.secretaria_ids.length === 0)) {
      alert('Por favor, selecione pelo menos uma secretaria ou marque "Acesso a todas as secretarias".');
      return;
    }

    const dataToSend = { ...formData };
    if (user && !dataToSend.password) {
      delete dataToSend.password;
    }

    // Filtrar valores inválidos de secretaria_ids
    if (dataToSend.secretaria_ids) {
      dataToSend.secretaria_ids = dataToSend.secretaria_ids.filter(id => id && id > 0);
    }

    onSave(dataToSend);
  };

  const selectedSecretariasNames = secretarias
    .filter(s => formData.secretaria_ids.includes(s.id))
    .map(s => s.secretaria);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Nome de Usuário"
        name="username"
        value={formData.username}
        onChange={handleChange}
        required
        disabled={!!user}
      />
      <Input
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        required
      />
      <Input
        label="Nome Completo"
        name="full_name"
        value={formData.full_name}
        onChange={handleChange}
      />
      <Input
        label={user ? "Nova Senha (deixe em branco para não alterar)" : "Senha"}
        name="password"
        type="password"
        value={formData.password}
        onChange={handleChange}
        required={!user}
        placeholder={user ? "••••••••" : "Mínimo 8 caracteres"}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Perfil <span className="text-red-500">*</span>
        </label>
        <select
          name="profile_id"
          value={formData.profile_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          required
          disabled={loading}
        >
          <option value="">Selecione um perfil</option>
          {profiles.map(profile => (
            <option key={profile.id} value={profile.id}>
              {profile.name} {profile.is_admin ? '(Admin)' : ''}
            </option>
          ))}
        </select>
      </div>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.allow_all_secretarias}
            onChange={handleChange}
            name="allow_all_secretarias"
            color="primary"
          />
        }
        label="Acesso a todas as secretarias"
      />

      {!loading && !formData.allow_all_secretarias && (
        <MultiSelectAutocomplete
          label="Secretarias"
          options={secretarias.map(s => s.secretaria)}
          value={selectedSecretariasNames}
          onChange={handleSecretariasChange}
          placeholder="Selecione as secretarias"
        />
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
          Ativo
        </label>
      </div>
      <div className="flex justify-end space-x-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" variant="primary">Salvar</Button>
      </div>
    </form>
  );
}